import { StatusConfig, StatusState } from '@gripp/shared-logic';
import { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { Colors } from '../../../themes';
import { colors, spacing, tokens } from '../../crop-circle/tokens';
import { ControlledTextInput, useFormContext } from '../../forms';
import { RoutineExecutionFormProps } from '../types';
import { OpenEndedResponse } from './types';

type OpenEndedResponseExecutionProps = RoutineExecutionFormProps<
  StatusConfig,
  StatusState
> & {
  useCropCircle?: boolean;
};

// TODO: Crop Circle Redesign | Remove after feature is fully rolled out
const OpenEndedResponseExecutionComponentOld: React.FC<
  OpenEndedResponseExecutionProps
> = (props: OpenEndedResponseExecutionProps) => {
  const openEndedResponseKey = OpenEndedResponse;
  const statusKey = 'status';
  const { control, setValue } = useFormContext();
  const { t } = useTranslation();
  const selectedStatus = useWatch({
    control,
    name: statusKey,
  });
  const isSelectedOpenEndedResponse = useMemo(
    () => selectedStatus === OpenEndedResponse,
    [selectedStatus]
  );

  useEffect(() => {
    if (
      props.currentState?.state?.openEndedResponse &&
      props?.config?.openEndedResponse
    ) {
      setValue(
        openEndedResponseKey,
        props.currentState.state.openEndedResponse,
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    }
  }, [props.currentState]);

  if (!props.config.openEndedResponse) return null;

  const onStatusChange = (status: string) => {
    setValue(statusKey, status, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <View style={{ marginHorizontal: 0, marginTop: 20 }}>
      <TouchableOpacity
        activeOpacity={0.7}
        style={[
          styles.button,
          !isSelectedOpenEndedResponse ? styles.buttonNotSelected : {},
        ]}
        onPress={() => {
          onStatusChange(OpenEndedResponse);
        }}
        disabled={props.isIssueEditRoutine}
      >
        {isSelectedOpenEndedResponse ? (
          <ControlledTextInput
            control={control}
            name={openEndedResponseKey}
            placeholder={t('routines.status.admin.otherStatus')}
            width={'100%'}
            inputInnerStyle={styles.currentStateButton}
            backgroundColor={'transparent'}
            autoFocus
          />
        ) : (
          <Text style={styles.buttonLabel}>
            {t('routines.status.admin.otherStatus')}
          </Text>
        )}
      </TouchableOpacity>
    </View>
  );
};

// TODO: Crop Circle Redesign | Remove to OpenEndedResponseExecutionComponentBase after feature is fully rolled out
const OpenEndedResponseExecutionComponentBase: React.FC<
  OpenEndedResponseExecutionProps
> = (props: OpenEndedResponseExecutionProps) => {
  const openEndedResponseKey = OpenEndedResponse;
  const statusKey = 'status';
  const { control, setValue } = useFormContext();
  const { t } = useTranslation();
  const selectedStatus = useWatch({
    control,
    name: statusKey,
  });
  const isSelectedOpenEndedResponse = useMemo(
    () => selectedStatus === OpenEndedResponse,
    [selectedStatus]
  );

  useEffect(() => {
    if (
      props.currentState?.state?.openEndedResponse &&
      props?.config?.openEndedResponse
    ) {
      setValue(
        openEndedResponseKey,
        props.currentState.state.openEndedResponse,
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    }
  }, [props.currentState]);

  if (!props.config.openEndedResponse) return null;

  const onStatusChange = (status: string) => {
    setValue(statusKey, status, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <View style={stylesCropCircle.container}>
      <Pressable
        style={[
          stylesCropCircle.button,
          isSelectedOpenEndedResponse && stylesCropCircle.currentStateButton,
        ]}
        onPress={() => {
          onStatusChange(OpenEndedResponse);
        }}
        disabled={props.isIssueEditRoutine}
      >
        {isSelectedOpenEndedResponse ? (
          <ControlledTextInput
            control={control}
            name={openEndedResponseKey}
            placeholder={t('routines.status.admin.otherStatus')}
            width={'100%'}
            inputInnerStyle={stylesCropCircle.inputInner}
            backgroundColor={'transparent'}
            autoFocus
          />
        ) : (
          <Text style={stylesCropCircle.buttonLabel}>
            {t('routines.status.admin.otherStatus')}
          </Text>
        )}
      </Pressable>
    </View>
  );
};

export const OpenEndedResponseExecutionComponent: React.FC<
  OpenEndedResponseExecutionProps
> = (props: OpenEndedResponseExecutionProps) => {
  if (props.useCropCircle) {
    return <OpenEndedResponseExecutionComponentBase {...props} />;
  }

  return <OpenEndedResponseExecutionComponentOld {...props} />;
};

const styles = StyleSheet.create({
  buttonsContainer: {
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  button: {
    height: 52,
    alignItems: 'center',
    justifyContent: 'center',
    color: Colors.white,
  },
  buttonNotSelected: {
    borderWidth: 2,
    borderRadius: 51,
    borderColor: Colors.blue,
  },
  currentStateButton: {
    borderWidth: 2,
    borderRadius: 51,
    borderColor: Colors.blue,
    backgroundColor: Colors.blue,
    color: Colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    width: '100%',
  },
  buttonLabel: {
    color: Colors.grayText,
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 19,
  },
  currentButtonLabel: {
    color: Colors.white,
  },
});

const stylesCropCircle = StyleSheet.create({
  container: {
    marginTop: spacing.sm,
  },
  button: {
    height: 52,
    borderRadius: tokens.borderRadius.sm,
    backgroundColor: colors.neutral[200],
    alignItems: 'center',
    justifyContent: 'center',
  },
  currentStateButton: {
    backgroundColor: colors.slate[600],
  },
  inputInner: {
    backgroundColor: colors.slate[600],
    borderRadius: tokens.borderRadius.sm,
    color: colors.brand.white,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    width: '100%',
  },
  buttonLabel: {
    ...tokens.typography.body.medium,
    color: colors.slate[700],
  },
});
